import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RefreshCw, Plus, Trash2, Package, TruckIcon, AlertTriangle, CheckCircle, Send, Printer, X, Edit2, Clock, ChevronDown, ChevronUp, Phone, Mail, FileText, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";
import { StockDualValue } from "@/components/Stock/StockDualValue";

const API_URL = __BACKEND_URL__;
const API = `${API_URL}/api`;

const PurchasingTab = ({ storeId: propStoreId }) => {
  // Use default store if not provided or empty
  const storeId = propStoreId || 'vencidinho_cangaiba';
  
  // State for suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [supplierForm, setSupplierForm] = useState({
    name: '', cnpj: '', contact_name: '', phone: '', email: '', 
    default_delivery_days: 7, credit_limit: '', payment_terms: '', notes: ''
  });
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [syncingSuppliers, setSyncingSuppliers] = useState(false);

  // State for forecasts
  const [forecasts, setForecasts] = useState([]);
  const [loadingForecasts, setLoadingForecasts] = useState(false);
  const [forecastParams, setForecastParams] = useState({ days_of_stock: 14, min_stock_days: 3 });
  const [forecastStats, setForecastStats] = useState({ total: 0, critical: 0, high: 0 });

  // State for forecasts by supplier
  const [forecastsBySupplier, setForecastsBySupplier] = useState([]);
  const [unassignedProducts, setUnassignedProducts] = useState([]);
  const [loadingBySupplier, setLoadingBySupplier] = useState(false);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [showZeroStock, setShowZeroStock] = useState(true);

  // State for runout alerts (7-day prediction)
  const [runoutAlerts, setRunoutAlerts] = useState([]);
  const [loadingRunout, setLoadingRunout] = useState(false);
  const [runoutStats, setRunoutStats] = useState({ critical: 0, high: 0, medium: 0, zero: 0, total: 0 });

  // Product details expansion state
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productDetails, setProductDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  // Purchase status tracking (em cotação, em compra)
  const [purchaseStatus, setPurchaseStatus] = useState({});

  // Load purchase status from localStorage on mount
  useEffect(() => {
    const savedStatus = localStorage.getItem('productPurchaseStatus');
    if (savedStatus) {
      try {
        setPurchaseStatus(JSON.parse(savedStatus));
      } catch (e) {
        console.error("Error loading purchase status:", e);
      }
    }
  }, []);

  // Save purchase status to localStorage when it changes
  useEffect(() => {
    if (Object.keys(purchaseStatus).length > 0) {
      localStorage.setItem('productPurchaseStatus', JSON.stringify(purchaseStatus));
    }
  }, [purchaseStatus]);

  // Toggle purchase status for a product
  const togglePurchaseStatus = (ean, field) => {
    setPurchaseStatus(prev => ({
      ...prev,
      [ean]: {
        ...prev[ean],
        [field]: !(prev[ean]?.[field] || false)
      }
    }));
  };

  // Toggle product details expansion and load data if needed
  const toggleProductDetails = async (ean) => {
    const isExpanding = !expandedProducts[ean];
    setExpandedProducts(prev => ({ ...prev, [ean]: isExpanding }));
    
    // Load details if expanding and not already loaded
    if (isExpanding && !productDetails[ean]) {
      setLoadingDetails(prev => ({ ...prev, [ean]: true }));
      try {
        const response = await axios.get(`${API}/stock/product-details/${ean}?store_id=vencidinho_cangaiba`);
        if (response.data.success) {
          setProductDetails(prev => ({ ...prev, [ean]: response.data }));
        }
      } catch (error) {
        console.error("Error loading product details:", error);
      } finally {
        setLoadingDetails(prev => ({ ...prev, [ean]: false }));
      }
    }
  };

  // State for orders
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderForm, setOrderForm] = useState({ supplier_id: '', expected_delivery_date: '', notes: '' });

  // Active tab
  const [activeSection, setActiveSection] = useState('by-supplier');

  // Load suppliers
  const loadSuppliers = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      const res = await fetch(`${API_URL}/api/purchasing/suppliers?store_id=${storeId}`);
      const data = await res.json();
      if (data.success) {
        setSuppliers(data.suppliers.filter(s => s.active !== false));
      }
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
    setLoadingSuppliers(false);
  }, [storeId]);

  // Sync suppliers from NFe
  const syncSuppliersFromNfe = async () => {
    setSyncingSuppliers(true);
    try {
      const res = await fetch(`${API_URL}/api/purchasing/suppliers/sync-from-nfe?store_id=${storeId}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        loadSuppliers();
        loadForecastsBySupplier();
        alert(`Sincronização concluída! ${data.created} novos fornecedores, ${data.updated} atualizados.`);
      }
    } catch (err) {
      console.error('Error syncing suppliers:', err);
      alert('Erro ao sincronizar fornecedores');
    }
    setSyncingSuppliers(false);
  };

  // Load forecasts by supplier
  const loadForecastsBySupplier = useCallback(async () => {
    setLoadingBySupplier(true);
    try {
      const res = await fetch(`${API_URL}/api/purchasing/forecasts/by-supplier?store_id=${storeId}&alert_days=7&include_zero_stock=true`);
      const data = await res.json();
      if (data.success) {
        setForecastsBySupplier(data.by_supplier || []);
        setUnassignedProducts(data.unassigned_products || []);
        setRunoutStats(data.stats || { critical: 0, high: 0, medium: 0, zero: 0, total: 0 });
        // Auto-expand suppliers with critical items or zero stock
        const expanded = {};
        data.by_supplier?.forEach(s => {
          if (s.stats.critical > 0 || s.stats.zero > 0) expanded[s.supplier.id] = true;
        });
        setExpandedSuppliers(expanded);
      }
    } catch (err) {
      console.error('Error loading forecasts by supplier:', err);
    }
    setLoadingBySupplier(false);
  }, [storeId]);

  // Load runout alerts (7-day prediction)
  const loadRunoutAlerts = useCallback(async () => {
    setLoadingRunout(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/runout-forecast?store_id=${storeId}&days=7`);
      const data = await res.json();
      if (data.success) {
        setRunoutAlerts(data.forecasts || []);
        setRunoutStats(data.stats || { critical: 0, high: 0, medium: 0, total: 0 });
      }
    } catch (err) {
      console.error('Error loading runout alerts:', err);
    }
    setLoadingRunout(false);
  }, [storeId]);

  // Load forecasts
  const loadForecasts = useCallback(async () => {
    setLoadingForecasts(true);
    try {
      const res = await fetch(
        `${API_URL}/api/purchasing/forecasts/auto-calculate?store_id=${storeId}&days_of_stock=${forecastParams.days_of_stock}&min_stock_days=${forecastParams.min_stock_days}`
      );
      const data = await res.json();
      if (data.success) {
        setForecasts(data.forecasts || []);
        setForecastStats({
          total: data.total_products || 0,
          critical: data.critical_count || 0,
          high: data.high_count || 0
        });
      }
    } catch (err) {
      console.error('Error loading forecasts:', err);
    }
    setLoadingForecasts(false);
  }, [storeId, forecastParams]);

  // Load orders
  const loadOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const res = await fetch(`${API_URL}/api/purchasing/orders?store_id=${storeId}`);
      const data = await res.json();
      if (data.success) {
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    }
    setLoadingOrders(false);
  }, [storeId]);

  useEffect(() => {
    loadSuppliers();
    loadForecasts();
    loadOrders();
    loadRunoutAlerts();
    loadForecastsBySupplier();
  }, [loadSuppliers, loadForecasts, loadOrders, loadRunoutAlerts, loadForecastsBySupplier]);

  // Toggle supplier expansion
  const toggleSupplierExpand = (supplierId) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplierId]: !prev[supplierId]
    }));
  };

  // Create/Update supplier
  const handleSaveSupplier = async () => {
    try {
      const url = editingSupplier
        ? `${API_URL}/api/purchasing/suppliers/${editingSupplier.id}`
        : `${API_URL}/api/purchasing/suppliers?store_id=${storeId}`;
      const method = editingSupplier ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(supplierForm)
      });
      const data = await res.json();
      if (data.success) {
        loadSuppliers();
        setShowSupplierForm(false);
        setEditingSupplier(null);
        setSupplierForm({ name: '', cnpj: '', contact_name: '', phone: '', email: '', default_delivery_days: 7, notes: '' });
      }
    } catch (err) {
      console.error('Error saving supplier:', err);
    }
  };

  // Delete supplier
  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Tem certeza que deseja remover este fornecedor?')) return;
    try {
      const res = await fetch(`${API_URL}/api/purchasing/suppliers/${supplierId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        loadSuppliers();
      }
    } catch (err) {
      console.error('Error deleting supplier:', err);
    }
  };

  // Edit supplier
  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name || '',
      cnpj: supplier.cnpj || '',
      contact_name: supplier.contact_name || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      default_delivery_days: supplier.default_delivery_days || 7,
      credit_limit: supplier.credit_limit || '',
      payment_terms: supplier.payment_terms || '',
      notes: supplier.notes || ''
    });
    setShowSupplierForm(true);
  };

  // Toggle item selection for order
  const toggleItemSelection = (forecast) => {
    const exists = selectedItems.find(i => i.ean === forecast.ean);
    if (exists) {
      setSelectedItems(selectedItems.filter(i => i.ean !== forecast.ean));
    } else {
      setSelectedItems([...selectedItems, {
        ean: forecast.ean,
        product_name: forecast.product_name,
        quantity: forecast.quantity_needed,
        cost_price: forecast.supplier?.cost_price || 0,
        unit: 'UN'
      }]);
    }
  };

  // Create order from selected items
  const handleCreateOrder = async () => {
    if (!orderForm.supplier_id || selectedItems.length === 0) {
      alert('Selecione um fornecedor e pelo menos um item');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/purchasing/orders?store_id=${storeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_id: orderForm.supplier_id,
          items: selectedItems,
          expected_delivery_date: orderForm.expected_delivery_date,
          notes: orderForm.notes
        })
      });
      const data = await res.json();
      if (data.success) {
        loadOrders();
        setSelectedItems([]);
        setShowOrderForm(false);
        setOrderForm({ supplier_id: '', expected_delivery_date: '', notes: '' });
        setActiveSection('orders');
      }
    } catch (err) {
      console.error('Error creating order:', err);
    }
  };

  // Update order status
  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/purchasing/orders/${orderId}/status?status=${status}`, {
        method: 'PUT'
      });
      const data = await res.json();
      if (data.success) {
        loadOrders();
      }
    } catch (err) {
      console.error('Error updating order status:', err);
    }
  };

  // Print order
  const handlePrintOrder = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/purchasing/orders/${orderId}/print`);
      const data = await res.json();
      if (data.success) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`<pre style="font-family: monospace; white-space: pre-wrap;">${data.print_text}</pre>`);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (err) {
      console.error('Error printing order:', err);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'confirmed': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      sent: 'Enviado',
      confirmed: 'Confirmado',
      delivered: 'Entregue',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const formatCurrency = (value) => {
    return value ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'N/A';
  };

  return (
    <div className="space-y-4" data-testid="purchasing-tab">
      {/* Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2 flex-wrap">
        <Button
          variant={activeSection === 'by-supplier' ? 'default' : 'outline'}
          onClick={() => setActiveSection('by-supplier')}
          className={`gap-2 ${runoutStats.critical > 0 ? 'animate-pulse' : ''}`}
          data-testid="by-supplier-section-btn"
        >
          <TruckIcon className="w-4 h-4" />
          Por Fornecedor ({forecastsBySupplier.length})
          {runoutStats.critical > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-600 text-white rounded-full">{runoutStats.critical}</span>
          )}
        </Button>
        <Button
          variant={activeSection === 'alerts' ? 'default' : 'outline'}
          onClick={() => setActiveSection('alerts')}
          className="gap-2"
          data-testid="alerts-section-btn"
        >
          <Clock className="w-4 h-4" />
          Alertas 7 Dias ({runoutStats.total || 0})
        </Button>
        <Button
          variant={activeSection === 'forecasts' ? 'default' : 'outline'}
          onClick={() => setActiveSection('forecasts')}
          className="gap-2"
          data-testid="forecasts-section-btn"
        >
          <AlertTriangle className="w-4 h-4" />
          Previsões ({forecastStats.total})
        </Button>
        <Button
          variant={activeSection === 'suppliers' ? 'default' : 'outline'}
          onClick={() => setActiveSection('suppliers')}
          className="gap-2"
          data-testid="suppliers-section-btn"
        >
          <TruckIcon className="w-4 h-4" />
          Fornecedores ({suppliers.length})
        </Button>
        <Button
          variant={activeSection === 'orders' ? 'default' : 'outline'}
          onClick={() => setActiveSection('orders')}
          className="gap-2"
          data-testid="orders-section-btn"
        >
          <Package className="w-4 h-4" />
          Pedidos ({orders.length})
        </Button>
      </div>

      {/* BY SUPPLIER SECTION */}
      {activeSection === 'by-supplier' && (
        <div className="space-y-4">
          {/* Sync Banner */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Sincronizar Fornecedores das NF-e</p>
                    <p className="text-sm text-blue-600">Importa automaticamente fornecedores, prazos e valores mínimos do histórico de notas</p>
                  </div>
                </div>
                <Button 
                  onClick={syncSuppliersFromNfe} 
                  disabled={syncingSuppliers}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncingSuppliers ? 'animate-spin' : ''}`} />
                  {syncingSuppliers ? 'Sincronizando...' : 'Sincronizar'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{runoutStats.critical || 0}</div>
                <div className="text-sm text-red-600">Crítico</div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-orange-600">{runoutStats.high || 0}</div>
                <div className="text-sm text-orange-600">Alto</div>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-yellow-600">{runoutStats.medium || 0}</div>
                <div className="text-sm text-yellow-600">Médio</div>
              </CardContent>
            </Card>
            <Card className="border-gray-300 bg-gray-100">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-gray-700">{runoutStats.zero || 0}</div>
                <div className="text-sm text-gray-600">Zerados</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{forecastsBySupplier.length}</div>
                <div className="text-sm text-gray-500">Fornecedores</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-purple-600">{unassignedProducts.length}</div>
                <div className="text-sm text-gray-500">Sem Fornecedor</div>
              </CardContent>
            </Card>
          </div>

          {/* Checkbox to show/hide zero stock */}
          <div className="flex items-center gap-2 bg-white p-3 rounded-lg border">
            <input
              type="checkbox"
              id="showZeroStock"
              checked={showZeroStock}
              onChange={(e) => setShowZeroStock(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="showZeroStock" className="text-sm font-medium text-gray-700 cursor-pointer">
              Mostrar produtos com estoque zerado ({runoutStats.zero || 0} produtos)
            </label>
          </div>

          {/* Suppliers List */}
          {loadingBySupplier ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : forecastsBySupplier.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <TruckIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">Nenhum produto para reposição</p>
                <p className="text-sm">Todos os produtos estão com estoque adequado</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {forecastsBySupplier.map((group) => (
                <Card key={group.supplier.id} className={`${group.stats.critical > 0 ? 'border-red-300' : ''}`}>
                  {/* Supplier Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleSupplierExpand(group.supplier.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${group.stats.critical > 0 ? 'bg-red-100' : 'bg-blue-100'}`}>
                          <TruckIcon className={`w-5 h-5 ${group.stats.critical > 0 ? 'text-red-600' : 'text-blue-600'}`} />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{group.supplier.name}</h3>
                          <div className="flex gap-4 text-sm text-gray-500">
                            {group.supplier.cnpj && <span>CNPJ: {group.supplier.cnpj}</span>}
                            <span>Prazo: {group.supplier.default_delivery_days} dias</span>
                            {group.supplier.nfe_stats?.min_valor_pedido && (
                              <span className="text-green-600 font-medium">
                                Pedido Mín: {formatCurrency(group.supplier.nfe_stats.min_valor_pedido)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Stats badges */}
                        <div className="flex gap-2 flex-wrap">
                          {group.stats.critical > 0 && (
                            <span className="px-2 py-1 bg-red-500 text-white rounded text-xs font-bold">
                              {group.stats.critical} crítico{group.stats.critical > 1 ? 's' : ''}
                            </span>
                          )}
                          {group.stats.high > 0 && (
                            <span className="px-2 py-1 bg-orange-500 text-white rounded text-xs font-bold">
                              {group.stats.high} alto{group.stats.high > 1 ? 's' : ''}
                            </span>
                          )}
                          {group.stats.zero > 0 && (
                            <span className="px-2 py-1 bg-gray-600 text-white rounded text-xs font-bold">
                              {group.stats.zero} zerado{group.stats.zero > 1 ? 's' : ''}
                            </span>
                          )}
                          <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                            {group.stats.total} produto{group.stats.total > 1 ? 's' : ''}
                          </span>
                        </div>
                        {/* Contact icons */}
                        {group.supplier.phone && (
                          <a href={`tel:${group.supplier.phone}`} className="p-2 hover:bg-gray-100 rounded">
                            <Phone className="w-4 h-4 text-gray-500" />
                          </a>
                        )}
                        {group.supplier.email && (
                          <a href={`mailto:${group.supplier.email}`} className="p-2 hover:bg-gray-100 rounded">
                            <Mail className="w-4 h-4 text-gray-500" />
                          </a>
                        )}
                        {expandedSuppliers[group.supplier.id] ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Products Table (Expanded) */}
                  {expandedSuppliers[group.supplier.id] && (
                    <div className="border-t px-4 pb-4">
                      {/* Products with stock (need reorder) */}
                      {group.products.length > 0 && (
                        <>
                          <h4 className="font-medium text-sm text-gray-700 mt-3 mb-2">Produtos para Reposição</h4>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2">Urgência</th>
                                <th className="text-left py-2">Produto</th>
                                <th className="text-right py-2">Estoque</th>
                                <th className="text-right py-2">Venda/Dia</th>
                                <th className="text-right py-2">Dias Rest.</th>
                                <th className="text-right py-2">Qtd. Sugerida</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.products.map((prod) => (
                                <tr key={prod.ean} className="border-b hover:bg-gray-50">
                                  <td className="py-2">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getUrgencyColor(prod.urgency)}`}>
                                      {prod.urgency === 'critical' ? 'CRÍTICO' : prod.urgency === 'high' ? 'ALTO' : 'MÉDIO'}
                                    </span>
                                  </td>
                                  <td className="py-2">
                                    <div className="font-medium">{prod.product_name}</div>
                                    <div className="text-xs text-gray-500">{prod.ean}</div>
                                  </td>
                                  <td className="py-2 text-right"><StockDualValue product={prod} inline size="sm" /></td>
                                  <td className="py-2 text-right">{prod.avg_daily_sales}</td>
                                  <td className="py-2 text-right font-bold" style={{ 
                                    color: prod.days_until_runout <= 1 ? '#dc2626' : prod.days_until_runout <= 3 ? '#ea580c' : '#ca8a04' 
                                  }}>
                                    {prod.days_until_runout} dias
                                  </td>
                                  <td className="py-2 text-right font-bold text-blue-600">{prod.recommended_qty}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {/* Zero stock products */}
                      {showZeroStock && group.zero_stock_products && group.zero_stock_products.length > 0 && (
                        <>
                          <h4 className="font-medium text-sm text-gray-700 mt-4 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 bg-gray-600 rounded-full"></span>
                            Produtos com Estoque Zerado ({group.zero_stock_products.length})
                          </h4>
                          <table className="w-full text-sm bg-gray-50 rounded">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 pl-2">Status</th>
                                <th className="text-left py-2">Produto</th>
                                <th className="text-right py-2">Venda/Dia</th>
                                <th className="text-right py-2 pr-2">Qtd. Sugerida</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.zero_stock_products.map((prod) => (
                                <tr key={prod.ean} className="border-b border-gray-200 hover:bg-gray-100">
                                  <td className="py-2 pl-2">
                                    <span className="px-2 py-1 bg-gray-600 text-white rounded text-xs font-medium">
                                      ZERADO
                                    </span>
                                  </td>
                                  <td className="py-2">
                                    <div className="font-medium text-gray-700">{prod.product_name}</div>
                                    <div className="text-xs text-gray-500">{prod.ean}</div>
                                  </td>
                                  <td className="py-2 text-right text-gray-600">{prod.avg_daily_sales}</td>
                                  <td className="py-2 text-right font-bold text-blue-600 pr-2">{prod.recommended_qty}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}

                      {/* No products message */}
                      {group.products.length === 0 && (!showZeroStock || !group.zero_stock_products || group.zero_stock_products.length === 0) && (
                        <div className="py-4 text-center text-gray-500 text-sm">
                          Nenhum produto para exibir
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}

              {/* Unassigned Products */}
              {unassignedProducts.length > 0 && (
                <Card className="border-purple-200">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleSupplierExpand('unassigned')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-purple-800">Produtos Sem Fornecedor</h3>
                          <p className="text-sm text-gray-500">Importe NF-e desses produtos para vincular ao fornecedor</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="px-2 py-1 bg-purple-200 text-purple-700 rounded text-xs">
                          {unassignedProducts.length} produto{unassignedProducts.length > 1 ? 's' : ''}
                        </span>
                        {expandedSuppliers['unassigned'] ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  {expandedSuppliers['unassigned'] && (
                    <div className="border-t px-4 pb-4">
                      <table className="w-full text-sm mt-3">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2">Produto</th>
                            <th className="text-right py-2">Estoque</th>
                            <th className="text-right py-2">Dias Rest.</th>
                            <th className="text-right py-2">Qtd. Sugerida</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unassignedProducts.map((prod) => (
                            <tr key={prod.ean} className="border-b hover:bg-gray-50">
                              <td className="py-2">
                                <div className="font-medium">{prod.product_name}</div>
                                <div className="text-xs text-gray-500">{prod.ean}</div>
                              </td>
                              <td className="py-2 text-right"><StockDualValue product={prod} inline size="sm" /></td>
                              <td className="py-2 text-right">{prod.days_until_runout} dias</td>
                              <td className="py-2 text-right font-bold text-blue-600">{prod.recommended_qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* ALERTS SECTION - 7 Day Runout Prediction */}
      {activeSection === 'alerts' && (
        <div className="space-y-4">
          {/* Alert Banner */}
          {runoutStats.critical > 0 && (
            <Card className="border-red-300 bg-gradient-to-r from-red-50 to-red-100">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-200 rounded-full animate-pulse">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-bold text-red-800 text-lg">
                      {runoutStats.critical} produto{runoutStats.critical > 1 ? 's' : ''} acabando em menos de 24h!
                    </p>
                    <p className="text-red-600 text-sm">
                      Providencie reposição imediata para evitar ruptura de estoque
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{runoutStats.critical || 0}</div>
                <div className="text-sm text-red-600">Crítico (&lt;1 dia)</div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-orange-600">{runoutStats.high || 0}</div>
                <div className="text-sm text-orange-600">Alto (1-3 dias)</div>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-yellow-600">{runoutStats.medium || 0}</div>
                <div className="text-sm text-yellow-600">Médio (3-7 dias)</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-slate-600">{runoutStats.total_at_risk || runoutStats.total || 0}</div>
                <div className="text-sm text-slate-500">Total em Risco</div>
              </CardContent>
            </Card>
          </div>

          {/* Runout Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-red-500" />
                Previsão de Ruptura (Próximos 7 Dias)
              </CardTitle>
              <Button onClick={loadRunoutAlerts} disabled={loadingRunout} variant="outline" size="sm">
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingRunout ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </CardHeader>
            <CardContent>
              {loadingRunout ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : runoutAlerts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                  <p className="font-medium text-green-600">Nenhum produto em risco de ruptura nos próximos 7 dias</p>
                  <p className="text-sm">Seu estoque está saudável!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Urgência</th>
                        <th className="text-left py-2">Produto</th>
                        <th className="text-center py-2">Em Cotação</th>
                        <th className="text-center py-2">Em Compra</th>
                        <th className="text-right py-2">Estoque Atual</th>
                        <th className="text-right py-2">Venda/Dia</th>
                        <th className="text-right py-2">Dias Restantes</th>
                        <th className="text-left py-2">Data Prevista</th>
                        <th className="text-right py-2">Qtd. Sugerida</th>
                        <th className="text-center py-2">Destaque</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runoutAlerts.map((alert) => (
                        <React.Fragment key={alert.ean}>
                          <tr 
                            className={`border-b hover:bg-gray-50 cursor-pointer ${expandedProducts[alert.ean] ? 'bg-blue-50' : ''}`} 
                            data-testid={`runout-alert-${alert.ean}`}
                            onClick={() => toggleProductDetails(alert.ean)}
                          >
                            <td className="py-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getUrgencyColor(alert.urgency)}`}>
                                {alert.urgency === 'critical' ? 'CRÍTICO' : alert.urgency === 'high' ? 'ALTO' : 'MÉDIO'}
                              </span>
                            </td>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                {expandedProducts[alert.ean] ? 
                                  <ChevronUp className="w-4 h-4 text-blue-500" /> : 
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                }
                                <div>
                                  <div className="font-medium">{alert.product_name || 'Sem nome'}</div>
                                  <div className="text-xs text-gray-500">{alert.ean}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox" 
                                checked={purchaseStatus[alert.ean]?.inQuote || false}
                                onChange={() => togglePurchaseStatus(alert.ean, 'inQuote')}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                title="Marcar como em cotação"
                              />
                            </td>
                            <td className="py-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox" 
                                checked={purchaseStatus[alert.ean]?.inPurchase || false}
                                onChange={() => togglePurchaseStatus(alert.ean, 'inPurchase')}
                                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                title="Marcar como em processo de compra"
                              />
                            </td>
                            <td className="py-2 text-right font-medium"><StockDualValue product={alert} inline size="sm" /></td>
                            <td className="py-2 text-right">{alert.avg_daily_sales}</td>
                            <td className="py-2 text-right">
                              <span className={`font-bold ${
                                alert.days_until_runout <= 1 ? 'text-red-600' : 
                                alert.days_until_runout <= 3 ? 'text-orange-600' : 'text-yellow-600'
                              }`}>
                                {alert.days_until_runout} dias
                              </span>
                            </td>
                            <td className="py-2 text-left text-gray-600">{alert.runout_date_formatted}</td>
                            <td className="py-2 text-right font-bold text-blue-600">{alert.recommended_order_qty}</td>
                            <td className="py-2 text-center">
                              {alert.is_highlighted && (
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">⭐ Destaque</span>
                              )}
                            </td>
                          </tr>
                          {/* Expanded Details Row */}
                          {expandedProducts[alert.ean] && (
                            <tr className="bg-gradient-to-r from-blue-50 to-slate-50">
                              <td colSpan={10} className="py-4 px-4">
                                {loadingDetails[alert.ean] ? (
                                  <div className="flex items-center justify-center py-4">
                                    <RefreshCw className="w-5 h-5 animate-spin text-blue-500 mr-2" />
                                    <span className="text-gray-500">Carregando detalhes...</span>
                                  </div>
                                ) : productDetails[alert.ean] ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Sales Last 7 Days */}
                                    <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4" />
                                        Vendas nos Últimos 7 Dias
                                      </h4>
                                      <div className="grid grid-cols-7 gap-1 mb-3">
                                        {productDetails[alert.ean].sales_7_days?.by_day?.map((day, idx) => (
                                          <div key={idx} className="text-center">
                                            <div className="text-[10px] text-gray-500 font-medium">{day.weekday}</div>
                                            <div className={`text-sm font-bold ${day.qty > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>
                                              {day.qty}
                                            </div>
                                            {day.avg_price > 0 && (
                                              <div className="text-[9px] text-gray-400">R${day.avg_price.toFixed(2)}</div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                      <div className="flex justify-between items-center pt-2 border-t border-blue-100">
                                        <div>
                                          <span className="text-xs text-gray-500">Total 7 dias:</span>
                                          <span className="ml-2 font-bold text-blue-700">
                                            {productDetails[alert.ean].sales_7_days?.total_qty || 0} un
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-xs text-gray-500">Média/dia:</span>
                                          <span className="ml-2 font-bold text-emerald-600">
                                            {productDetails[alert.ean].sales_7_days?.avg_daily_qty || 0}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-xs text-gray-500">Faturamento:</span>
                                          <span className="ml-2 font-bold text-purple-600">
                                            R$ {(productDetails[alert.ean].sales_7_days?.total_value || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Purchase History */}
                                    <div className="bg-white rounded-lg p-4 border border-amber-200 shadow-sm">
                                      <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                                        <Package className="w-4 h-4" />
                                        Últimos 4 Preços de Compra
                                      </h4>
                                      {productDetails[alert.ean].purchase_history?.length > 0 ? (
                                        <>
                                          <div className="space-y-2">
                                            {productDetails[alert.ean].purchase_history.map((purchase, idx) => (
                                              <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-amber-100 last:border-0">
                                                <div className="flex-1">
                                                  <div className="text-xs text-gray-500">{purchase.date}</div>
                                                  <div className="text-gray-700 truncate">{purchase.supplier}</div>
                                                </div>
                                                <div className="text-right">
                                                  <div className="font-bold text-amber-700">
                                                    R$ {purchase.unit_cost?.toFixed(2)}
                                                  </div>
                                                  <div className="text-xs text-gray-400">{purchase.quantity} un</div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                          <div className="mt-3 pt-2 border-t border-amber-200 flex justify-between items-center">
                                            <span className="text-xs text-gray-500">Preço médio:</span>
                                            <span className="font-bold text-amber-700">
                                              R$ {productDetails[alert.ean].avg_purchase_price?.toFixed(2) || '0.00'}
                                            </span>
                                          </div>
                                        </>
                                      ) : (
                                        <div className="text-center py-4 text-gray-400">
                                          <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                          <p className="text-sm">Nenhum histórico de compra encontrado</p>
                                          <p className="text-xs">Importe NF-e deste produto para registrar</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-4 text-gray-400">
                                    Não foi possível carregar os detalhes
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* FORECASTS SECTION */}
      {activeSection === 'forecasts' && (
        <div className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{forecastStats.critical}</div>
                <div className="text-sm text-red-600">Críticos (≤1 dia)</div>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-orange-600">{forecastStats.high}</div>
                <div className="text-sm text-orange-600">Alta (≤3 dias)</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{forecastStats.total}</div>
                <div className="text-sm text-gray-500">Total para Repor</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-600">{selectedItems.length}</div>
                <div className="text-sm text-gray-500">Selecionados</div>
              </CardContent>
            </Card>
          </div>

          {/* Forecast Parameters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label className="text-xs">Dias de Estoque Alvo</Label>
                  <Input
                    type="number"
                    value={forecastParams.days_of_stock}
                    onChange={(e) => setForecastParams({ ...forecastParams, days_of_stock: parseInt(e.target.value) || 14 })}
                    className="w-24"
                    data-testid="days-of-stock-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Alerta Mínimo (dias)</Label>
                  <Input
                    type="number"
                    value={forecastParams.min_stock_days}
                    onChange={(e) => setForecastParams({ ...forecastParams, min_stock_days: parseInt(e.target.value) || 3 })}
                    className="w-24"
                    data-testid="min-stock-days-input"
                  />
                </div>
                <Button onClick={loadForecasts} disabled={loadingForecasts} className="gap-2" data-testid="refresh-forecasts-btn">
                  <RefreshCw className={`w-4 h-4 ${loadingForecasts ? 'animate-spin' : ''}`} />
                  Recalcular
                </Button>
                {selectedItems.length > 0 && (
                  <Button onClick={() => setShowOrderForm(true)} className="gap-2 bg-green-600 hover:bg-green-700" data-testid="create-order-btn">
                    <Plus className="w-4 h-4" />
                    Criar Pedido ({selectedItems.length} itens)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Forecasts Table */}
          <Card>
            <CardHeader>
              <CardTitle>Produtos para Reposição</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingForecasts ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : forecasts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum produto precisa de reposição no momento
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems(forecasts.map(f => ({
                                  ean: f.ean,
                                  product_name: f.product_name,
                                  quantity: f.quantity_needed,
                                  cost_price: f.supplier?.cost_price || 0,
                                  unit: 'UN'
                                })));
                              } else {
                                setSelectedItems([]);
                              }
                            }}
                            checked={selectedItems.length === forecasts.length && forecasts.length > 0}
                          />
                        </th>
                        <th className="text-left py-2">Urgência</th>
                        <th className="text-left py-2">Produto</th>
                        <th className="text-right py-2">Estoque</th>
                        <th className="text-right py-2">Venda/Dia</th>
                        <th className="text-right py-2">Dias Rest.</th>
                        <th className="text-right py-2">Qtd. Sugerida</th>
                        <th className="text-left py-2">Fornecedor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forecasts.slice(0, 50).map((forecast) => (
                        <tr key={forecast.ean} className="border-b hover:bg-gray-50" data-testid={`forecast-row-${forecast.ean}`}>
                          <td className="py-2 px-2">
                            <input
                              type="checkbox"
                              checked={selectedItems.some(i => i.ean === forecast.ean)}
                              onChange={() => toggleItemSelection(forecast)}
                            />
                          </td>
                          <td className="py-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getUrgencyColor(forecast.urgency)}`}>
                              {forecast.urgency === 'critical' ? 'CRÍTICO' : forecast.urgency === 'high' ? 'ALTO' : 'MÉDIO'}
                            </span>
                          </td>
                          <td className="py-2">
                            <div className="font-medium">{forecast.product_name || 'Sem nome'}</div>
                            <div className="text-xs text-gray-500">{forecast.ean}</div>
                          </td>
                          <td className="py-2 text-right"><StockDualValue product={forecast} inline size="sm" /></td>
                          <td className="py-2 text-right">{forecast.avg_daily_sales}</td>
                          <td className="py-2 text-right font-medium" style={{ color: forecast.days_remaining <= 1 ? 'red' : forecast.days_remaining <= 3 ? 'orange' : 'inherit' }}>
                            {forecast.days_remaining}
                          </td>
                          <td className="py-2 text-right font-bold text-blue-600">{forecast.quantity_needed}</td>
                          <td className="py-2">
                            {forecast.has_supplier ? (
                              <span className="text-green-600 text-xs">Cadastrado</span>
                            ) : (
                              <span className="text-gray-400 text-xs">Não cadastrado</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {forecasts.length > 50 && (
                    <div className="text-center py-2 text-gray-500 text-sm">
                      Exibindo 50 de {forecasts.length} produtos
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* SUPPLIERS SECTION */}
      {activeSection === 'suppliers' && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Fornecedores</CardTitle>
              <Button onClick={() => { setShowSupplierForm(true); setEditingSupplier(null); setSupplierForm({ name: '', cnpj: '', contact_name: '', phone: '', email: '', default_delivery_days: 7, credit_limit: '', payment_terms: '', notes: '' }); }} className="gap-2" data-testid="add-supplier-btn">
                <Plus className="w-4 h-4" />
                Novo Fornecedor
              </Button>
            </CardHeader>
            <CardContent>
              {loadingSuppliers ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : suppliers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum fornecedor cadastrado
                </div>
              ) : (
                <div className="grid gap-4">
                  {suppliers.map((supplier) => (
                    <div key={supplier.id} className="border rounded-lg p-4 flex justify-between items-start" data-testid={`supplier-card-${supplier.id}`}>
                      <div>
                        <div className="font-medium text-lg">{supplier.name}</div>
                        {supplier.cnpj && <div className="text-sm text-gray-500">CNPJ: {supplier.cnpj}</div>}
                        {supplier.contact_name && <div className="text-sm">Contato: {supplier.contact_name}</div>}
                        <div className="flex gap-4 text-sm text-gray-600 mt-1">
                          {supplier.phone && <span>Tel: {supplier.phone}</span>}
                          {supplier.email && <span>Email: {supplier.email}</span>}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">Prazo padrão: {supplier.default_delivery_days} dias</div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEditSupplier(supplier)} data-testid={`edit-supplier-${supplier.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteSupplier(supplier.id)} className="text-red-600" data-testid={`delete-supplier-${supplier.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ORDERS SECTION */}
      {activeSection === 'orders' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pedidos de Compra</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingOrders ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum pedido de compra
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div key={order.id} className="border rounded-lg p-4" data-testid={`order-card-${order.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-bold text-lg">{order.order_number}</div>
                          <div className="text-sm text-gray-600">{order.supplier_name}</div>
                          <div className="text-xs text-gray-400">
                            Criado em: {new Date(order.created_at).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusLabel(order.status)}
                          </span>
                          <div className="text-lg font-bold mt-2">R$ {order.total_cost?.toFixed(2)}</div>
                        </div>
                      </div>
                      
                      <div className="text-sm mb-3">
                        <span className="text-gray-500">Itens: </span>
                        <span className="font-medium">{order.total_items}</span>
                        <span className="text-gray-500 ml-4">Previsão: </span>
                        <span className="font-medium">{order.expected_delivery_date}</span>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        {order.status === 'pending' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'sent')} className="gap-1">
                              <Send className="w-3 h-3" /> Marcar Enviado
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')} className="gap-1 text-red-600">
                              <X className="w-3 h-3" /> Cancelar
                            </Button>
                          </>
                        )}
                        {order.status === 'sent' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'confirmed')} className="gap-1">
                            <CheckCircle className="w-3 h-3" /> Confirmar
                          </Button>
                        )}
                        {order.status === 'confirmed' && (
                          <Button size="sm" variant="outline" onClick={() => handleUpdateOrderStatus(order.id, 'delivered')} className="gap-1 text-green-600">
                            <CheckCircle className="w-3 h-3" /> Marcar Entregue
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handlePrintOrder(order.id)} className="gap-1">
                          <Printer className="w-3 h-3" /> Imprimir
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* SUPPLIER FORM MODAL */}
      {showSupplierForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="supplier-form-modal">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowSupplierForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={supplierForm.name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                  placeholder="Nome do fornecedor"
                  data-testid="supplier-name-input"
                />
              </div>
              <div>
                <Label>CNPJ</Label>
                <Input
                  value={supplierForm.cnpj}
                  onChange={(e) => setSupplierForm({ ...supplierForm, cnpj: e.target.value })}
                  placeholder="00.000.000/0000-00"
                  data-testid="supplier-cnpj-input"
                />
              </div>
              <div>
                <Label>Contato</Label>
                <Input
                  value={supplierForm.contact_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })}
                  placeholder="Nome do contato"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div>
                  <Label>Prazo de Entrega (dias)</Label>
                  <Input
                    type="number"
                    value={supplierForm.default_delivery_days}
                    onChange={(e) => setSupplierForm({ ...supplierForm, default_delivery_days: parseInt(e.target.value) || 7 })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Limite de Crédito (R$)</Label>
                  <Input
                    type="number"
                    value={supplierForm.credit_limit}
                    onChange={(e) => setSupplierForm({ ...supplierForm, credit_limit: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Prazo de Pagamento (dias)</Label>
                  <Input
                    type="number"
                    value={supplierForm.payment_terms}
                    onChange={(e) => setSupplierForm({ ...supplierForm, payment_terms: e.target.value })}
                    placeholder="Ex: 30"
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                  placeholder="email@fornecedor.com"
                />
              </div>
              <div>
                <Label>Observações</Label>
                <Input
                  value={supplierForm.notes}
                  onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
                  placeholder="Notas adicionais"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowSupplierForm(false)}>Cancelar</Button>
                <Button onClick={handleSaveSupplier} disabled={!supplierForm.name} data-testid="save-supplier-btn">
                  {editingSupplier ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ORDER FORM MODAL */}
      {showOrderForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="order-form-modal">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Criar Pedido de Compra</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowOrderForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Fornecedor *</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={orderForm.supplier_id}
                  onChange={(e) => setOrderForm({ ...orderForm, supplier_id: e.target.value })}
                  data-testid="order-supplier-select"
                >
                  <option value="">Selecione um fornecedor</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Data Prevista de Entrega</Label>
                <Input
                  type="date"
                  value={orderForm.expected_delivery_date}
                  onChange={(e) => setOrderForm({ ...orderForm, expected_delivery_date: e.target.value })}
                  data-testid="order-date-input"
                />
              </div>
              <div>
                <Label>Observações</Label>
                <Input
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                  placeholder="Notas adicionais"
                />
              </div>
              
              <div className="border rounded p-3 bg-gray-50">
                <div className="font-medium mb-2">Itens Selecionados ({selectedItems.length})</div>
                <div className="max-h-40 overflow-y-auto text-sm">
                  {selectedItems.map((item) => (
                    <div key={item.ean} className="flex justify-between py-1 border-b">
                      <span>{item.product_name || item.ean}</span>
                      <span className="font-medium">{item.quantity} un</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowOrderForm(false)}>Cancelar</Button>
                <Button 
                  onClick={handleCreateOrder} 
                  disabled={!orderForm.supplier_id || selectedItems.length === 0}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-order-btn"
                >
                  Criar Pedido
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PurchasingTab;
